import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, AlertCircle, CheckCircle, Printer } from 'lucide-react';
import axios from 'axios';
import { Button, StatusBadge, FileUploadManager } from '../common/ui';
import { useAuth } from '../../contexts/AuthContext';
import DeviceActivity from './DeviceActivity';
import { DeviceModules } from '../repairs/components';
import { printPdfBlob } from '../../utils/pdfUtils';
import {
  DeviceInfoSection,
  OwnerInfoSection,
  ProductAttributesSection,
  RepairsHistorySection
} from './components';

const DeviceViewPage = () => {
  const { serialNumber, deviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const activityRef = useRef();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Device data
  const [deviceData, setDeviceData] = useState(null);
  const [productConfig, setProductConfig] = useState({
    product: null,
    productAttributes: [],
    assignedModules: [],
    moduleAttributesMap: {}
  });
  const [deviceAttributeValues, setDeviceAttributeValues] = useState({});
  const [moduleAttributeValues, setModuleAttributeValues] = useState({});

  // Repairs history
  const [repairs, setRepairs] = useState([]);
  const [repairsLoading, setRepairsLoading] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [ownershipData, setOwnershipData] = useState(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);

  const fetchDeviceData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const url = deviceId
        ? `/api/devices/${deviceId}`
        : `/api/devices/serial/${serialNumber}`;
      const response = await axios.get(url);
      const device = response.data;
      
      // Parse meta if it's a string (JSONB from database might be stringified)
      if (device.meta && typeof device.meta === 'string') {
        try {
          device.meta = JSON.parse(device.meta);
        } catch (e) {
          console.warn('Failed to parse device meta:', e);
          device.meta = null;
        }
      }
      
      setDeviceData(device);
      
      // Parse attributes into a map
      const attrMap = {};
      (device.attributes || []).forEach(attr => {
        attrMap[attr.name] = attr.value;
      });
      setDeviceAttributeValues(attrMap);
      
      // Parse module attributes into a map
      const moduleAttrMap = {};
      (device.moduleAttributes || []).forEach(attr => {
        const key = `${attr.module_id}_${attr.attribute_name}`;
        moduleAttrMap[key] = attr.attribute_value;
      });
      setModuleAttributeValues(moduleAttrMap);
      
      // Fetch product configuration - prefer product_name over model
      const productName = device.product_name || device.model;
      if (productName) {
        await fetchProductConfiguration(productName);
      }
      
    } catch (err) {
      console.error('Error fetching device:', err);
      setError('Failed to load device data');
    } finally {
      setLoading(false);
    }
  }, [deviceId, serialNumber]);

  const fetchProductConfiguration = async (productName) => {
    try {
      const response = await axios.get(`/api/devices/product-config/${encodeURIComponent(productName)}`, {
        params: { includeHidden: 'true' }
      });
      
      console.log('Product config fetched:', {
        productName,
        product: response.data.product,
        productAttributesCount: response.data.productAttributes?.length || 0,
        productAttributes: response.data.productAttributes
      });
      
      setProductConfig({
        product: response.data.product,
        productAttributes: response.data.productAttributes || [],
        assignedModules: response.data.assignedModules || [],
        moduleAttributesMap: response.data.moduleAttributesMap || {}
      });
    } catch (err) {
      console.error('Error fetching product configuration:', err);
      console.error('Product name used:', productName);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
    }
  };

  const fetchRepairsHistory = useCallback(async () => {
    if (!deviceData?.id) return;
    try {
      setRepairsLoading(true);
      
      const response = await axios.get(`/api/repairs?device_id=${deviceData.id}&limit=100`);
      
      const repairsData = response.data.repairs || response.data;
      if (Array.isArray(repairsData)) {
        setRepairs(repairsData);
      } else {
        setRepairs([]);
      }
    } catch (error) {
      console.error('Error fetching repairs history:', error);
      setRepairs([]);
    } finally {
      setRepairsLoading(false);
    }
  }, [deviceData?.id]);

  useEffect(() => {
    if (deviceId || serialNumber) {
      fetchDeviceData();
    }
  }, [fetchDeviceData, deviceId, serialNumber]);

  useEffect(() => {
    if (deviceId && deviceData?.serial_number) {
      navigate(`/devices/${deviceData.serial_number}/view`, { replace: true });
    }
  }, [deviceId, deviceData?.serial_number, navigate]);

  useEffect(() => {
    fetchRepairsHistory();
  }, [fetchRepairsHistory]);

  useEffect(() => {
    const serial = deviceData?.serial_number;
    if (!serial) return;
    let cancelled = false;
    setOwnershipLoading(true);
    setOwnershipData(null);
    axios
      .get(`/api/ownership?serials=${encodeURIComponent(serial)}`)
      .then((res) => {
        if (!cancelled) {
          setOwnershipData(res.data.results?.[serial] ?? null);
          setOwnershipLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOwnershipData(null);
          setOwnershipLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [deviceData?.serial_number]);

  // Save device information
  const handleDeviceInfoSave = async (deviceInfo) => {
    try {
      setError('');
      setSuccess('');
      
      await axios.put(`/api/devices/${deviceData.id}`, {
        serial_number: deviceInfo.serial_number,
        product_id: deviceInfo.product_id || deviceData.product_id, // Required by backend
        status: deviceInfo.status,
        purchase_date: deviceInfo.purchase_date !== undefined ? deviceInfo.purchase_date : deviceData.purchase_date,
        warranty_expiry: deviceData.warranty_expiry,
        notes: deviceInfo.notes !== undefined ? deviceInfo.notes : deviceData.notes,
        performed_by: user?.id || null
      });
      
      // Refresh activity
      if (activityRef.current) {
        activityRef.current.refresh();
      }
      
      setSuccess('Device information saved successfully');
      await fetchDeviceData();
    } catch (err) {
      console.error('Error saving device information:', err);
      const errorMsg = err.response?.data?.error || 'Failed to save device information';
      setError(errorMsg);
      throw err;
    }
  };

  // Save device attributes
  const handleAttributesSave = async (attributeValues) => {
    try {
      setError('');
      setSuccess('');
      
      // Save device attributes
      for (const attr of productConfig.productAttributes) {
        const value = attributeValues[attr.name] || '';
        if (attr.name) {
          await axios.post(`/api/devices/${deviceData.id}/attributes`, {
            name: attr.name,
            value: value,
            type: attr.type,
            performed_by: user?.id || null
          });
        }
      }
      
      // Refresh activity
      if (activityRef.current) {
        activityRef.current.refresh();
      }
      
      setSuccess('Device attributes saved successfully');
      await fetchDeviceData();
    } catch (err) {
      console.error('Error saving device attributes:', err);
      const errorMsg = err.response?.data?.error || 'Failed to save device attributes';
      setError(errorMsg);
      throw err;
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this device? This action cannot be undone.')) {
      try {
        await axios.delete(`/api/devices/${deviceData.id}`);
        navigate('/devices');
      } catch (err) {
        console.error('Error deleting device:', err);
        setError('Failed to delete device');
      }
    }
  };

  const handleModuleAction = (action, module) => {
    console.log(`${action} module:`, module);
    // Module actions are handled internally by DeviceModules component
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !deviceData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/devices')}
            variant="secondary"
            className="mt-4"
            icon={ArrowLeft}
          >
            Back to Devices
          </Button>
        </div>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-600">Device not found</div>
        </div>
      </div>
    );
  }

  const handleGenerateLabel = async () => {
    if (!deviceData?.id) return;
    
    try {
      setGeneratingLabel(true);
      setError('');
      setSuccess('');

      // If device is in draft status, just change status to print_label (no PDF download)
      // and navigate to /devices page
      if (deviceData.status === 'draft') {
        try {
          await axios.put(`/api/devices/${deviceData.id}`, {
            serial_number: deviceData.serial_number,
            product_id: deviceData.product_id,
            status: 'print_label',
            purchase_date: deviceData.purchase_date,
            warranty_expiry: deviceData.warranty_expiry,
            notes: deviceData.notes,
            owner_name: deviceData.owner_name,
            owner_email: deviceData.owner_email,
            performed_by: user?.id || null
          });
          
          // Navigate to devices page after successful status change
          navigate('/devices?status=draft');
          return;
        } catch (statusError) {
          console.error('Error updating device status:', statusError);
          setError('Failed to update device status to Print label.');
          return;
        }
      }

      // For non-draft devices, generate and download the PDF
      const response = await axios.post(`/api/devices/${deviceData.id}/generate-label-pdf`, {}, {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.details || 'Failed to generate label PDF');
      }

      const blob = response.data;
      await printPdfBlob(blob);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `device-label-${deviceData.serial_number}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      setSuccess(`Label PDF generated successfully: ${filename}`);

    } catch (error) {
      console.error('Error generating label PDF:', error);
      let errorMessage = 'Failed to generate label PDF';
      if (error.response?.data) {
        errorMessage = error.response.data.error || error.response.data.details || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setGeneratingLabel(false);
    }
  };

  // Action buttons configuration
  const getActionButtons = () => {
    return {
      backButton: (
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="xs"
          icon={ArrowLeft}
          className="flex-shrink-0 px-0"
        >
          Back
        </Button>
      ),
      actionButtons: [
        <Button
          key="delete"
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          icon={Trash2}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          Delete
        </Button>,
        ...(deviceData?.order_id ? [
          <Button
            key="edit-order"
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/orders/${deviceData.order_id}/serials`)}
            icon={Edit}
            title="Edit order assignment"
          >
            Edit Order
          </Button>
        ] : [])
      ],
      ctaButton: (
        <Button
          variant="primary"
          size="md"
          onClick={handleGenerateLabel}
          icon={Printer}
          loading={generatingLabel}
        >
          Print Label
        </Button>
      )
    };
  };

  // Action buttons component (similar to RepairActionButtons)
  const ActionButtons = ({ backButton, actionButtons = [], ctaButton }) => {
    const validActionButtons = actionButtons?.filter(Boolean) || [];
    const hasAnyButtons = backButton || validActionButtons.length > 0 || ctaButton;

    if (!hasAnyButtons) {
      return null;
    }

    return (
      <div className="space-y-3">
        {/* Mobile layout - stacked rows */}
        <div className="block md:hidden">
          {/* Row 1: Back button */}
          {backButton && (
            <div className="mb-3">
              {backButton}
            </div>
          )}
          
          {/* Row 2: Action buttons + CTA */}
          {(validActionButtons.length > 0 || ctaButton) && (
            <div className="flex flex-col sm:flex-row gap-3">
              {validActionButtons.map((button, index) => (
                <div key={index} className="flex-1">
                  {React.cloneElement(button, {
                    className: `${button.props.className || ''} w-full`.trim()
                  })}
                </div>
              ))}
              {ctaButton && (
                <div className="flex-1">
                  {React.cloneElement(ctaButton, {
                    className: `${ctaButton.props.className || ''} w-full`.trim()
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop layout - horizontal */}
        <div className="hidden md:flex md:items-center md:justify-between">
          {/* Left side: Back button */}
          <div>
            {backButton}
          </div>

          {/* Right side: Action buttons + CTA */}
          {(validActionButtons.length > 0 || ctaButton) && (
            <div className="flex gap-3 items-center">
              {validActionButtons.map((button, index) => (
                <React.Fragment key={index}>
                  {button}
                </React.Fragment>
              ))}
              {ctaButton}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-4">
          {/* Header */}
          <div className="space-y-3">
            {/* 1. Title and status */}
            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {deviceData.product_name || deviceData.model || 'Device'} • {deviceData.serial_number}
              </h1>
              <StatusBadge status={deviceData.status} variant="device" />
            </div>
            
            {/* 2. Action Buttons */}
            <ActionButtons {...getActionButtons()} />
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success</h3>
                  <div className="mt-2 text-sm text-green-700">{success}</div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-4 space-y-4">
              
              {/* Device Information */}
              <DeviceInfoSection 
                deviceData={deviceData} 
                readOnly={false}
                onSave={handleDeviceInfoSave}
                warrantySince={ownershipData?.warranty?.warranty_since ?? null}
              />

              {/* Owner Information */}
              <OwnerInfoSection
                ownership={ownershipData}
                ownershipLoading={ownershipLoading}
                deviceData={deviceData}
              />

              {/* Device attributes */}
              <ProductAttributesSection 
                productAttributes={productConfig.productAttributes}
                deviceAttributeValues={deviceAttributeValues}
                readOnly={false}
                deviceStatus={deviceData.status}
                onSave={handleAttributesSave}
                deviceMeta={deviceData.meta}
              />

              {/* Device Modules */}
              <DeviceModules 
                repairData={{ device_id: deviceData.id, id: null }}
                showActions={true}
                onModuleAction={handleModuleAction}
                productConfig={productConfig}
                deviceStatus={deviceData.status}
                deviceAttributeValues={deviceAttributeValues}
                onRepairSaved={() => {
                  // Refresh activity when a module repair is saved
                  if (activityRef.current) {
                    activityRef.current.refresh();
                  }
                }}
              />

              {/* Repairs History */}
              <RepairsHistorySection 
                deviceData={deviceData}
                repairs={repairs}
                loading={repairsLoading}
              />

              {/* Attachments */}
              <div className="border-t border-gray-200 pt-3">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Attachments</h2>
                <FileUploadManager
                  entityType="devices"
                  entityId={deviceData.id}
                  compact
                />
              </div>

            </div>
          </div>
          
          {/* Duplicate Action Buttons - Bottom (outside white card, in gray area) */}
          <div className="mt-4">
            <ActionButtons {...getActionButtons()} />
          </div>

          {/* Device Activity */}
          <div className="mt-4">
            <DeviceActivity 
              ref={activityRef} 
              deviceId={deviceData.id}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceViewPage;

